import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  lettersTransformed: number;
  wordsProcessed: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  timestamp: Date;
  textLength: number;
  lettersCount: number;
}

const ROT13CipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [includeNumbers, setIncludeNumbers] = useState<boolean>(false);
  const [includeSpecialChars, setIncludeSpecialChars] = useState<boolean>(false);
  const [customRotation, setCustomRotation] = useState<number>(13);
  const [useCustomRotation, setUseCustomRotation] = useState<boolean>(false);
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    lettersTransformed: 0,
    wordsProcessed: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);

  // ROT13/ROTn cipher implementation
  const rotCipher = useCallback((text: string): string => {
    if (!text) return '';
    
    const rotation = useCustomRotation ? customRotation : 13;
    const processedText = removeSpaces ? text.replace(/\s/g, '') : text;
    
    let result = processedText.split('').map(char => {
      if (/[a-zA-Z]/.test(char)) {
        const isUpperCase = char === char.toUpperCase();
        const base = isUpperCase ? 65 : 97;
        const charCode = char.charCodeAt(0) - base;
        const rotatedCode = (charCode + rotation) % 26;
        const newChar = String.fromCharCode(rotatedCode + base);
        return preserveCase ? newChar : (isUpperCase ? newChar.toLowerCase() : newChar);
      } else if (includeNumbers && /[0-9]/.test(char)) {
        // For numbers: rotate 0-9
        const digit = parseInt(char);
        const rotatedDigit = (digit + (rotation % 10)) % 10;
        return rotatedDigit.toString();
      } else if (includeSpecialChars && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(char)) {
        const specialChars = '!@#$%^&*()_+-=[]{};\'":\\|,.<>/?';
        const index = specialChars.indexOf(char);
        if (index !== -1) {
          const rotatedIndex = (index + rotation) % specialChars.length;
          return specialChars[rotatedIndex];
        }
      }
      return char;
    }).join('');
    
    // Group output if enabled
    if (groupOutput && result) {
      result = result.replace(/\s/g, ''); // Remove all spaces first
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [preserveCase, includeNumbers, includeSpecialChars, useCustomRotation, customRotation, removeSpaces, groupOutput, groupSize]);

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    const result = rotCipher(inputText);
    setOutputText(result);
    return result;
  }, [inputText, rotCipher]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result) return;
    
    const lettersCount = inputText.replace(/[^a-zA-Z]/g, '').length;
    const wordsCount = inputText.trim().split(/\s+/).filter(word => word.length > 0).length;
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      timestamp: new Date(),
      textLength: inputText.length,
      lettersCount: lettersCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setStats(prevStats => {
      const newTotalOps = prevStats.totalOperations + 1;
      const newTotalChars = prevStats.totalCharactersProcessed + inputText.length;
      const newLettersTransformed = prevStats.lettersTransformed + lettersCount;
      const newWordsProcessed = prevStats.wordsProcessed + wordsCount;
      
      return {
        ...prevStats,
        totalOperations: newTotalOps,
        totalCharactersProcessed: newTotalChars,
        averageTextLength: Math.round(newTotalChars / newTotalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        lettersTransformed: newLettersTransformed,
        wordsProcessed: newWordsProcessed
      };
    });
  }, [inputText]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText]); // Removed processText from dependencies

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setCustomRotation(13);
    setUseCustomRotation(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText);
    setOutputText(temp);
  };

  const generateSampleText = () => {
    const samples = [
      "HELLO WORLD",
      "THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG",
      "ROT13 IS A SIMPLE LETTER SUBSTITUTION CIPHER",
      "CRYPTOGRAPHY PROTECTS INFORMATION FROM UNAUTHORIZED ACCESS",
      "USENET NEWSGROUPS USED ROT13 TO HIDE SPOILERS",
      "NOPQRSTUVWXYZABCDEFGHIJKLM"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const applyROT = (rotation: number) => {
    setCustomRotation(rotation);
    setUseCustomRotation(rotation !== 13);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>ROT13 Cipher - Classic Letter Substitution Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional ROT13 Cipher tool with real-time letter substitution encryption. Supports custom rotations (ROT1-ROT25), analytics, and educational features. Perfect for Usenet, puzzles, and cryptography learning." />
        <meta name="keywords" content="rot13 cipher, rot13 encoder, rot13 decoder, letter substitution cipher, caesar cipher rot13, usenet rot13, spoiler protection, cryptography tool, encryption decoder, puzzle cipher, online rot13, text encoder" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ROT13 Cipher - Classic Letter Substitution Tool" />
        <meta property="og:description" content="Professional ROT13 Cipher with real-time processing, custom rotations, and educational features. Free online letter substitution cipher tool for Usenet, puzzles, and learning." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="ROT13 Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ROT13 Cipher - Letter Substitution Tool" />
        <meta name="twitter:description" content="Professional ROT13 Cipher with custom rotations and real-time processing. Free cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@rot13cipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="ROT13 Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Educational Keywords */}
        <meta name="subject" content="Cryptography, Computer Science, Internet Culture, Puzzles" />
        <meta name="classification" content="Education, Technology, Entertainment" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="ROT13 Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ROT13 Cipher Tool",
            "alternateName": ["ROT13 Encoder Decoder", "Letter Substitution Cipher", "Usenet ROT13 Tool"],
            "description": "Advanced ROT13 Cipher tool for letter substitution encryption and decryption with custom rotations, real-time processing, and educational features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "UtilityApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time ROT13 encryption and decryption",
              "Custom rotation support (ROT1-ROT25)",
              "Letter substitution cipher",
              "Usenet spoiler protection",
              "Analytics and usage statistics",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design",
              "Educational cipher information",
              "Puzzle and game support"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "ROT13 Cipher Tool",
              "description": "Educational cryptography and utility tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Computer Science and Internet Culture Education",
              "targetName": "Basic Cryptography and Internet History"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "practice", "entertainment"],
            "typicalAgeRange": "12-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.7",
              "ratingCount": "3421",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "Computer Science Teacher"
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                },
                "reviewBody": "Perfect tool for teaching basic cryptography concepts. Students love the real-time feedback and custom rotation features."
              }
            ]
          })}
        </script>
        
        {/* Additional Structured Data for Educational Content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "ROT13 Cipher Interactive Learning Tool",
            "description": "Learn about letter substitution ciphers through hands-on interaction with the ROT13 cipher and its variations.",
            "educationalLevel": "beginner",
            "teaches": [
              "Letter substitution ciphers",
              "ROT13 encryption method",
              "Caesar cipher variations",
              "Internet culture and Usenet history",
              "Basic cryptography principles"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "practice", "entertainment"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "developer"],
              "audienceType": ["student", "general public", "programmer"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Cryptography"
              },
              {
                "@type": "Thing", 
                "name": "Internet Culture"
              },
              {
                "@type": "Thing",
                "name": "Computer Science"
              }
            ]
          })}
        </script>
        
        {/* Internet Culture Context Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DigitalDocument",
            "name": "ROT13 in Internet Culture",
            "description": "Digital tool representing the ROT13 cipher's role in early Internet culture, particularly in Usenet newsgroups for spoiler protection.",
            "about": {
              "@type": "Thing",
              "name": "Internet History"
            },
            "mentions": [
              {
                "@type": "Thing",
                "name": "Usenet"
              },
              {
                "@type": "Thing",
                "name": "Spoiler Protection"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <RefreshCw className="text-blue-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">ROT13 Cipher</h1>
                <p className="text-sm opacity-60">Letter Substitution Cipher Tool</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Rotation</label>
                  <select
                    value={useCustomRotation ? 'custom' : '13'}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setUseCustomRotation(true);
                      } else {
                        setUseCustomRotation(false);
                        setCustomRotation(parseInt(e.target.value));
                      }
                    }}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="13">ROT13 (Standard)</option>
                    <option value="1">ROT1</option>
                    <option value="5">ROT5</option>
                    <option value="25">ROT25</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                {useCustomRotation && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Custom ROT</label>
                    <input
                      type="number"
                      value={customRotation}
                      onChange={(e) => setCustomRotation(Number(e.target.value))}
                      min="1"
                      max="25"
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                )}
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Shuffle size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <label className={`w-full p-2 rounded border cursor-pointer flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}>
                    <Upload size={16} />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              
              {/* Quick ROT Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm font-medium">Quick ROT:</span>
                {[1, 3, 5, 7, 13, 17, 21, 25].map(rot => (
                  <button
                    key={rot}
                    onClick={() => applyROT(rot)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      (useCustomRotation ? customRotation : 13) === rot
                        ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                        : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')
                    }`}
                  >
                    ROT{rot}
                  </button>
                ))}
              </div>
              
              {/* Alphabet Reference */}
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h4 className="text-sm font-medium mb-2">ROT{useCustomRotation ? customRotation : 13} Alphabet Reference:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-xs">
                  <div>
                    <div className="text-blue-500">Plain:  A B C D E F G H I J K L M</div>
                    <div className="text-green-500">ROT{useCustomRotation ? customRotation : 13}: {
                      'ABCDEFGHIJKLM'.split('').map(char => 
                        String.fromCharCode(((char.charCodeAt(0) - 65 + (useCustomRotation ? customRotation : 13)) % 26) + 65)
                      ).join(' ')
                    }</div>
                  </div>
                  <div>
                    <div className="text-blue-500">Plain:  N O P Q R S T U V W X Y Z</div>
                    <div className="text-green-500">ROT{useCustomRotation ? customRotation : 13}: {
                      'NOPQRSTUVWXYZ'.split('').map(char => 
                        String.fromCharCode(((char.charCodeAt(0) - 65 + (useCustomRotation ? customRotation : 13)) % 26) + 65)
                      ).join(' ')
                    }</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encode or decode using ROT13..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'rot13-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <RefreshCw className="text-blue-500" />
                  <span>About ROT13 Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is ROT13?</h3>
                  <p>ROT13 is a simple letter substitution cipher that replaces each letter with the letter 13 positions after it in the alphabet. It's a special case of the Caesar cipher with a shift of 13. The beauty of ROT13 is that it's its own inverse - applying ROT13 twice returns the original text.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Take each letter in the alphabet</li>
                    <li>Move 13 positions forward (A→N, B→O, C→P, etc.)</li>
                    <li>If you go past Z, wrap around to the beginning (N→A, O→B, etc.)</li>
                    <li>Non-alphabetic characters remain unchanged</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs`}>
                    <div className="text-blue-500">Original: HELLO WORLD</div>
                    <div className="text-green-500">ROT13:    URYYB JBEYQ</div>
                    <div className="text-blue-500">ROT13:    HELLO WORLD</div>
                    <div className="text-xs opacity-60 mt-1">(Applying ROT13 twice gives back the original)</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Internet Culture & Usenet</h3>
                  <ul className="space-y-1">
                    <li><strong>Spoiler Protection:</strong> Used in Usenet newsgroups to hide spoilers in movie and book discussions</li>
                    <li><strong>Offensive Content:</strong> Used to obscure potentially offensive jokes or content</li>
                    <li><strong>Email Headers:</strong> Sometimes found in email systems for basic obfuscation</li>
                    <li><strong>Online Forums:</strong> Still used today in various online communities for spoiler protection</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Why 13?</h3>
                  <p>The number 13 is special because it's exactly half of 26 (the number of letters in the English alphabet). This makes ROT13 self-inverse: encoding and decoding use the same operation. ROT13(ROT13(text)) = text.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Other ROT Variants</h3>
                  <ul className="space-y-1">
                    <li><strong>ROT5:</strong> For numbers (0-9), shifts by 5</li>
                    <li><strong>ROT47:</strong> Includes numbers and symbols, shifts by 47 in ASCII</li>
                    <li><strong>ROT1-ROT25:</strong> Any shift value for different security levels</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Educational Value</h3>
                  <ul className="space-y-1">
                    <li>Introduction to cryptography concepts</li>
                    <li>Understanding modular arithmetic</li>
                    <li>Learning about Caesar ciphers</li>
                    <li>Internet history and culture</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-yellow-600">⚠️ <strong>No Security:</strong> ROT13 provides no cryptographic security and can be broken instantly. It's designed for obfuscation, not encryption. Never use for actual security purposes.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Numbers (ROT5)</label>
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Special Characters</label>
                  <input
                    type="checkbox"
                    checked={includeSpecialChars}
                    onChange={(e) => setIncludeSpecialChars(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="number"
                      value={groupSize}
                      onChange={(e) => setGroupSize(Number(e.target.value))}
                      min="2"
                      max="10"
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group (e.g., 5 = "HELLO WORLD")
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Quick ROT Settings</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 5, 13, 17, 21, 25].map(rot => (
                      <button
                        key={rot}
                        onClick={() => applyROT(rot)}
                        className={`p-2 text-xs rounded border transition-colors ${
                          (useCustomRotation ? customRotation : 13) === rot
                            ? (isDarkMode ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-500 border-blue-400 text-white')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                        }`}
                      >
                        ROT{rot}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Usenet Style</h3>
                  <button
                    onClick={() => {
                      setPreserveCase(true);
                      setIncludeNumbers(false);
                      setIncludeSpecialChars(false);
                      setRemoveSpaces(false);
                      setGroupOutput(false);
                      applyROT(13);
                    }}
                    className={`w-full p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                  >
                    Apply Usenet Defaults
                  </button>
                  <div className="text-xs opacity-60 mt-1">
                    Classic ROT13 settings used in Usenet newsgroups
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.lettersTransformed}</div>
                  <div className="text-sm">Letters Transformed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.wordsProcessed}</div>
                  <div className="text-sm">Words Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Total Characters</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Average Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs px-2 py-1 rounded bg-blue-500 text-white">
                            ROT{useCustomRotation ? customRotation : 13}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-green-500 text-white">
                            {item.lettersCount} letters
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.textLength} chars
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <RefreshCw size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encoding or decoding to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Usage Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-blue-500">Character Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.lettersTransformed / stats.totalCharactersProcessed) * 100) : 0}%</div>
                    </div>
                    <div>
                      <div className="text-green-500">Avg Letters/Word:</div>
                      <div>{stats.wordsProcessed > 0 ? Math.round(stats.lettersTransformed / stats.wordsProcessed) : 0}</div>
                    </div>
                    <div>
                      <div className="text-purple-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Operations/Session:</div>
                      <div>{Math.round(stats.totalOperations / Math.max(1, history.length))}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ROT13CipherApp;